import {
  RichTextFragment,
  HardwareProductDetailsVariantFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { Bundle } from '@/ui/services/BundleService';
import { ICartState } from '@/ui/store/cart/types';
import { SelectedProductVariants } from '@/ui/components/pages/ProductDetail/types/SelectedProductVariants';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { CartUpsellingItemSection } from '@/ui/components/layout/cart/CartAddOnServices/CartUpsellingItemSection/types/CartUpsellingItemSection';
import { generateRandomId } from '@/ui/utils/helpers';
import { DeliveryTimeRange } from '@/integrations/crystallize/types/localization/DeliveryTimeRange';
import { HardwareProduct } from './HardwareProductService';
import { SmartlockCrystallizeComponents } from '../components/pages/ProductDetail/SmartlockDetail/types/SmartlockCrystallizeComponents';
import { CylinderDetail } from '../components/pages/ProductDetail/SmartlockDetail/types/CylinderDetail';
import { ParsedSmartlockData } from '../components/pages/ProductDetail/SmartlockDetail/types/ParsedSmartlockData';
import { ParsedSmartlockVariant } from '../components/pages/ProductDetail/SmartlockDetail/types/ParsedSmartlockVariant';
import { ParsedProductVariant } from '../components/pages/ProductDetail/types/ParsedProductVariant';
import { BundleHostSlot } from '../components/pages/ProductDetail/types/bundle-host-slot';
import { ManufacturerOption } from '../components/pages/ProductDetail/SmartlockDetail/DoorKnobSection/types/ManufacturerOption';
import { SmartlockCrystallizeData } from '../components/pages/ProductDetail/SmartlockDetail/types/SmartlockCrystallizeData';
import { parseCartUpsellingContent } from '../components/pages/ProductDetail/SmartlockDetail/utils/parseCartUpsellingContent';

export class Smartlock
  extends HardwareProduct<SmartlockCrystallizeData, ParsedSmartlockVariant>
  implements ParsedSmartlockData {
  cylinderHeading?: RichTextFragment;

  doorKnob?: any;

  cartUpselling?: CartUpsellingItemSection[];

  checkoutUpselling?: any;

  components: SmartlockCrystallizeComponents;

  availableCylinders: CylinderDetail[];

  variants: ParsedSmartlockVariant[];

  subscriptionHeading?: RichTextFragment;

  subscriptionBundles?: Bundle[] | null;

  alternativeModelSubscriptionHeading?: RichTextFragment;

  upgradeHeading?: RichTextFragment;

  upgradeBundles?: Bundle[] | null;

  allowUpgradeChooser: boolean;

  constructor(data: SmartlockCrystallizeData, cartStore: ICartState) {
    super(data, cartStore);
    this.cylinderHeading =
      componentByTypename(data.cylinderHeading?.content, 'RichTextContent') || undefined;
    this.doorKnob = data.doorKnob || undefined;
    this.cartUpselling = parseCartUpsellingContent(data.cartExtraServices);
    this.checkoutUpselling = data.upsellingProducts;
    this.components = data.components;
    this.variants = data.variants?.map(this.parseProductVariant) || [];
    this.availableCylinders = this.extractUniqueRelationTiles(
      this.variants,
      (variant) => variant.cylinder,
    );
    this.subscriptionHeading =
      componentByTypename(data.subscriptionBundlesHeading?.content, 'RichTextContent') || undefined;
    this.subscriptionBundles = this.parseBundles(data.subscriptionBundles);
    this.alternativeModelSubscriptionHeading =
      componentByTypename(data.alternativeModelSubscriptionHeading?.content, 'RichTextContent') ||
      undefined;
    this.upgradeHeading =
      componentByTypename(data.upgradeBundlesHeading?.content, 'RichTextContent') || undefined;
    this.upgradeBundles = this.parseBundles(data.upgradeBundles);
    this.allowUpgradeChooser =
      componentByTypename(data.allowUpgradeChooser?.content, 'BooleanContent')?.value || false;
  }

  protected parseProductVariant(
    rawVariant: HardwareProductDetailsVariantFragment,
  ): ParsedSmartlockVariant {
    const baseVariant = super.parseProductVariant.call(this, rawVariant);
    return {
      ...baseVariant,
      cylinder: this.parseRelationTileDetail(rawVariant.components, 'cylinder'),
    };
  }

  getSelectedVariant(options: { color?: string; cylinder?: string }) {
    const selectedVariant = this.variants.find(
      (variant) =>
        variant.color?.name === options.color && variant.cylinder?.type === options.cylinder,
    );
    return selectedVariant;
  }

  /**
   * The Smart Lock's row in a bundle configurator: one tile per cylinder the host
   * cylinder picker shows, keyed by the SKU of that cylinder in the current colour.
   * A cylinder that does not exist in the current colour gets no tile. Choosing a
   * tile goes through the chooser's own setter, so the host picker, the `sku` URL
   * parameter and the bundle slot follow. Without a tile there is nothing to mirror,
   * so the host gets no row of its own.
   */
  getBundleHostSlot(
    currentVariant: ParsedSmartlockVariant,
    visibleCylinders: CylinderDetail[],
    selectCylinder: (color: string | undefined, cylinderType: string) => void,
  ): BundleHostSlot | undefined {
    const colorName = currentVariant.color?.name;
    const tiles = visibleCylinders.flatMap((cylinder) => {
      const variant = this.getSelectedVariant({ color: colorName, cylinder: cylinder.type });
      return variant ? [{ ...cylinder, type: variant.sku }] : [];
    });

    if (tiles.length < 1) {
      return undefined;
    }

    return {
      productId: this.id,
      attributeLabel: 'cylinder',
      tiles,
      selectSku: (sku: string) => {
        const cylinderType = this.getVariantBySKU(sku)?.cylinder?.type;
        if (cylinderType) {
          selectCylinder(colorName, cylinderType);
        }
      },
    };
  }

  addToCart(
    currentVariant: ParsedProductVariant,
    options: {
      hasDoorKnob?: boolean;
      selectedManufacturer?: ManufacturerOption;
      selectedBundle?: Bundle;
      selectedBundleProductVariants?: SelectedProductVariants;
      upgradeProgramDeviceId?: string;
      directPurchase?: boolean;
      quantity?: number;
      // Layouts that sell the add-ons on the page itself skip the chooser the
      // cart would otherwise open after the Smart Lock lands in it.
      skipCartUpselling?: boolean;
    },
  ): void {
    if (!options.selectedBundle && !options.skipCartUpselling) {
      this.handleCartUpselling(this.cartUpselling);
    }

    if (options.hasDoorKnob && options.selectedManufacturer) {
      this.addDoorknobAdapterToCart(
        options.selectedManufacturer,
        this.getDeliveryTimeRange(currentVariant),
      );
    }

    this.addItemsToCart(currentVariant, options);

    if (options.directPurchase) {
      return;
    }

    this.cartStore.toggleCartDisplay();
  }

  findAttachedBundleByExtRef(extRef: string): Bundle | undefined {
    const all = [
      ...(this.bundles ?? []),
      ...(this.upgradeBundles ?? []),
      ...(this.subscriptionBundles ?? []),
    ];
    return all.find((bundle) => bundle?.documentExtRef === extRef);
  }

  private addDoorknobAdapterToCart(
    selectedManufacturer: ManufacturerOption,
    deliveryTime: DeliveryTimeRange,
  ): void {
    const adapterCartItem = {
      id: selectedManufacturer.id,
      url: selectedManufacturer.path,
      shape: selectedManufacturer.shape,
      ...selectedManufacturer.knobAdapterVariant,
      quantity: 1,
    };

    if (!adapterCartItem.priceVariant) {
      // TODO err - missing price variant for adapter?
      return;
    }
    this.cartStore.addCartItem({
      cartItemId: generateRandomId(),
      ...adapterCartItem,
      priceVariant: adapterCartItem.priceVariant!,
      deliveryTime, // use delivery time from smartlock
    });
  }
}
